import React from 'react';
import { Page } from '../../components';
import DhobaraPageLayout from '../../components/DhobaraPageLayout/DhobaraPageLayout';
import SHIPPING_TIERS from '../../config/shipping-tiers.json';
import css from './ShippingPage.module.css';

// Rendered from the same file that charges the buyer and labels the seller's
// package-size picker, so this page cannot drift from what is actually
// charged - which is exactly how it came to advertise a flat $20.95.
const defaultTier = SHIPPING_TIERS.tiers.find(t => t.size === SHIPPING_TIERS.defaultPackageSize);

const ShippingPage = () => (
  <Page
    title="Shipping Policy | Dhobara"
    description="Learn how Dhobara ships orders — UPS Ground delivery, costs, timeframes, tracking, and seller shipping obligations for sold items."
    scrollingDisabled={false}
  >
  <DhobaraPageLayout>
    <div className={css.pageHeader}>
      <span className={css.eyebrow}>Policies</span>
      <h1 className={css.pageTitle}>Shipping Policy</h1>
    </div>
    <div className={css.content}>
      <p className={css.intro}>
        Dhobara ships all orders via UPS within the continental United States. Here's everything you need to know about how your order gets to you.
      </p>

      <h2 className={css.sectionHeading}>Shipping Carrier &amp; Cost</h2>
      <p className={css.para}>All orders are shipped via <strong>UPS Ground</strong>, with tracking included.</p>
      <p className={css.para}>Shipping is a flat fee added at checkout. It depends on the size of parcel the piece needs — a dupatta and a bridal lehenga don't cost the same to move — so the rate is set by the garment rather than by the price of the item:</p>
      <ul className={css.bullets}>
        {SHIPPING_TIERS.tiers.map(t => (
          <li key={t.size}>
            <strong>${t.priceInSubunits / 100} &mdash; {t.label}.</strong>{' '}
            {t.examples.charAt(0).toUpperCase() + t.examples.slice(1)}.
          </li>
        ))}
      </ul>
      <p className={css.para}>The seller chooses the package size when listing the piece, so the exact shipping fee is shown at checkout before you pay &mdash; you'll never be surprised by it. Where no size has been set, the {defaultTier ? defaultTier.label : 'Medium'} rate applies.</p>

      <h2 className={css.sectionHeading}>Delivery Timeframe</h2>
      <p className={css.para}>Once a sale is confirmed, sellers have <strong>2 business days</strong> to ship using the prepaid UPS label provided by Dhobara. After the item is picked up by UPS, delivery typically takes <strong>3–5 business days</strong>.</p>
      <p className={css.para}>Total estimated delivery time from order confirmation: <strong>5–7 business days</strong>.</p>

      <h2 className={css.sectionHeading}>Shipping Area</h2>
      <p className={css.para}>Dhobara currently ships within the <strong>United States only</strong>. We do not ship to PO Boxes, military addresses (APO/FPO), or US territories at this time. International shipping is not available.</p>

      <h2 className={css.sectionHeading}>Order Tracking</h2>
      <p className={css.para}>Once your order has been shipped, you will receive an email with your UPS tracking number. You can track your shipment at <a href="https://www.ups.com/track?loc=en_US" target="_blank" rel="noopener noreferrer">ups.com/track</a>.</p>

      <h2 className={css.sectionHeading}>Lost or Damaged Packages</h2>
      <p className={css.para}>Once an item is scanned by UPS, responsibility for transit lies with the carrier. If your package arrives damaged or is lost in transit, please contact us at <a href="mailto:admin@dhobara.com">admin@dhobara.com</a> immediately and we will help you file a claim with UPS.</p>

      <h2 className={css.sectionHeading}>For Sellers: Shipping Your Sold Item</h2>
      <p className={css.para}>When your listing sells, Dhobara will email you a prepaid UPS shipping label. You are responsible for:</p>
      <ul className={css.bullets}>
        <li>Packaging the item securely within 2 business days of receiving the label</li>
        <li>Using only the Dhobara-provided UPS label (no other carriers or labels accepted)</li>
        <li>Dropping the sealed package off at any UPS location</li>
        <li>Photographing the sealed package before drop-off</li>
      </ul>
      <p className={css.para}>Failure to ship within 2 business days may result in the sale being voided and your listing being suspended. See our <a href="/seller-terms">Seller Terms &amp; Conditions</a> for full details.</p>

      <h2 className={css.sectionHeading}>Questions?</h2>
      <p className={css.para}>Contact us at <a href="mailto:admin@dhobara.com">admin@dhobara.com</a> and we'll be happy to help.</p>
    </div>
  </DhobaraPageLayout>
  </Page>
);

export default ShippingPage;
